import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, useSpring, useTransform } from 'framer-motion';
import { ShieldCheck, Users, Target } from 'lucide-react';
const values = [{
  icon: ShieldCheck,
  title: 'Money-Back Guarantee',
  prefix: '100%',
  hasCounter: true,
  description: 'We offer a 100% money-back guarantee, and since our inception in 2003 have never had a client ask for money back.'
}, {
  icon: Users,
  title: 'High-Quality, High-Touch',
  prefix: null,
  hasCounter: false,
  description: 'We limit the number of searches we conduct, and we hold weekly status calls to provide complete visibility into our process and progress.'
}, {
  icon: Target,
  title: 'Completion Rate',
  prefix: '100%',
  hasCounter: true,
  description: 'All of our searches have resulted in successful placements (with the exception of searches that were terminated due to the successful exit of our client during the search).'
}];
function Counter({
  from,
  to
}: {
  from: number;
  to: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const spring = useSpring(from, {
    mass: 0.8,
    stiffness: 75,
    damping: 15
  });
  const display = useTransform(spring, current => Math.round(current));
  useEffect(() => {
    if (inView) {
      spring.set(to);
    }
  }, [inView, spring, to]);
  return <motion.span ref={ref} data-id="element-128">{display}</motion.span>;
}
export function ValueProps() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, {
    once: true,
    margin: '-100px'
  });
  return <section className="py-24 bg-gray-50" id="about" data-id="element-129">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-130">
        <div className="text-center mb-20" data-id="element-131">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-132">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 uppercase tracking-widest mb-6" data-id="element-133">
              Building Tomorrow's Best Companies
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto mb-6" data-id="element-134"></div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-135">
              Our commitment to excellence ensures we deliver the leadership
              talent that drives your future success.
            </p>
          </motion.div>
        </div>

        <div ref={containerRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12" data-id="element-136">
          {values.map((value, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 40,
          scale: 0.95
        }} animate={isInView ? {
          opacity: 1,
          y: 0,
          scale: 1
        } : {}} transition={{
          duration: 0.6,
          delay: index * 0.2,
          ease: 'easeOut'
        }} className="relative bg-white p-8 rounded-xl shadow-sm hover:shadow-xl transition-shadow duration-300 border-l-4 border-primary overflow-hidden group" data-id="element-137">
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity duration-300" data-id="element-138">
                <value.icon className="w-32 h-32 text-primary transform rotate-12" data-id="element-139" />
              </div>

              <div className="relative z-10" data-id="element-140">
                <motion.div initial={{
              scale: 0
            }} animate={isInView ? {
              scale: 1
            } : {}} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15,
              delay: 0.4 + index * 0.2
            }} className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-6 text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300" data-id="element-141">
                  <value.icon className="w-8 h-8" strokeWidth={2} data-id="element-142" />
                </motion.div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3 flex items-baseline gap-2" data-id="element-143">
                  {value.hasCounter ? <span className="text-4xl text-primary font-extrabold" data-id="element-144">
                      <Counter from={0} to={100} data-id="element-145" />%
                    </span> : null}
                  <span className={value.hasCounter ? 'text-lg' : 'text-xl'} data-id="element-146">
                    {value.title}
                  </span>
                </h3>

                <p className="text-gray-600 leading-relaxed" data-id="element-147">
                  {value.description}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}